import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, LogOut, Mail, RefreshCw } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import { Button, Card, CardContent } from '@/components/ui';
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const { user, setUser, sendVerificationEmail, signOut, fetchUserData, isLoading } = useAuthStore();
  const [countdown, setCountdown] = useState(0);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);

  const syncVerifiedStatus = useCallback(async (uid: string) => {
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return;

      const status = userSnap.data().status as string | undefined;
      if (status === 'pending') {
        await updateDoc(userRef, {
          status: 'email_verified',
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error('Failed to sync users.status after email verification:', error);
    }
  }, []);

  const reflectVerifiedState = useCallback(async () => {
    if (!auth.currentUser || !user) {
      return false;
    }

    await auth.currentUser.reload();
    if (!auth.currentUser.emailVerified) {
      return false;
    }

    await syncVerifiedStatus(user.uid);
    await fetchUserData(user.uid);
    setUser({
      ...user,
      email: auth.currentUser.email,
      emailVerified: true,
    });
    toast.success('メール認証が完了しました');
    navigate('/dashboard', { replace: true });
    return true;
  }, [fetchUserData, navigate, setUser, syncVerifiedStatus, user]);

  useEffect(() => {
    const interval = setInterval(() => {
      void reflectVerifiedState();
    }, 5000);

    return () => clearInterval(interval);
  }, [reflectVerifiedState]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [navigate, user]);

  const handleResendEmail = async () => {
    try {
      await sendVerificationEmail();
      toast.success('認証メールを再送信しました');
      setCountdown(60);
    } catch {
      toast.error('メール送信に失敗しました');
    }
  };

  const handleCheckVerification = async () => {
    setIsCheckingVerification(true);
    try {
      const verified = await reflectVerifiedState();
      if (!verified) {
        toast.info('メールはまだ認証されていません');
      }
    } catch {
      toast.error('確認中にエラーが発生しました');
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch {
      // Error is handled in authStore
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#F5EFE5] px-4 py-8">
      <div className="pointer-events-none absolute -top-20 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-copper/15 blur-3xl" />
      <div className="pointer-events-none absolute right-[-84px] top-[28%] h-64 w-64 rounded-full bg-[#E6D0B4]/70 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-96px] left-[-96px] h-72 w-72 rounded-full bg-[#D8C3A5]/55 blur-3xl" />

      <div className="relative mx-auto flex min-h-[calc(100dvh-4rem)] w-full max-w-lg items-center">
        <Card className="w-full animate-fade-in overflow-hidden border-[#E8DCCB] shadow-[0_26px_80px_rgba(70,45,25,0.13)]">
          <div className="h-1.5 bg-gradient-to-r from-[#C9955E] via-[#B87333] to-[#D3A577]" />
          <CardContent className="space-y-6 px-6 py-7 sm:px-8">
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F4E7D6]">
                <Mail className="h-8 w-8 text-copper" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#9B8D78]">
                  Email Verification
                </p>
                <h1 className="mt-2 text-2xl font-heading-luxury text-[#1A1A1A]">
                  メールアドレスを確認してください
                </h1>
              </div>
              <p className="rounded-2xl border border-[#E9DDCE] bg-[#FCF8F1] px-4 py-3 text-sm leading-relaxed text-[#6E6557]">
                <span className="font-medium text-[#1A1A1A]">{user.email}</span>
                <br />
                宛に確認メールを送信しています。
              </p>
            </div>

            <div className="space-y-3 rounded-2xl border border-[#EEE4D7] bg-[#FEFBF7] p-4 text-sm text-[#6D6456]">
              <p className="font-medium text-[#1A1A1A]">確認手順</p>
              <p className="flex items-start gap-2">
                <span className="mt-1 block h-1.5 w-1.5 rounded-full bg-copper" />
                メール本文のリンクを開く
              </p>
              <p className="flex items-start gap-2">
                <span className="mt-1 block h-1.5 w-1.5 rounded-full bg-copper" />
                この画面で「認証状態を確認」を押す
              </p>
              <p className="flex items-start gap-2">
                <span className="mt-1 block h-1.5 w-1.5 rounded-full bg-copper" />
                認証完了後にダッシュボードへ移動
              </p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={handleCheckVerification}
                fullWidth
                isLoading={isCheckingVerification}
                leftIcon={<RefreshCw className="h-4 w-4" />}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                認証状態を確認
              </Button>
              <Button
                variant="secondary"
                onClick={handleResendEmail}
                fullWidth
                disabled={countdown > 0}
                isLoading={isLoading}
              >
                {countdown > 0
                  ? `${countdown}秒後に再送信できます`
                  : '認証メールを再送信'}
              </Button>
            </div>

            <button
              onClick={handleSignOut}
              className="mx-auto flex items-center gap-2 text-sm text-[#7F7566] transition hover:text-copper"
            >
              <LogOut className="h-4 w-4" />
              別のアカウントでログイン
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
